/* eslint react-hooks/exhaustive-deps: "warn" */

import { useContext } from 'react';

import { ContentContext } from 'context/ContentContext';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { Faq } from 'ui/atoms/Faq/Faq';
import { Section } from 'ui/molecules/Section/Section';
import { HeroSection } from 'ui/molecules/HeroSection/HeroSection';
import { OurCustomersV2 } from 'ui/molecules/OurCustomers/OurCustomersV2';
import { BankVerifyHeroDemo } from 'ui/molecules/BankVerifyHeroDemo/BankVerifyHeroDemo';
import { VerificationLayers } from 'ui/molecules/VerificationLayers/VerificationLayers';
import { RiskScanDemo } from 'ui/molecules/RiskScanDemo/RiskScanDemo';
import { BankVerificationCard } from 'ui/molecules/BankVerificationCard/BankVerificationCard';
import { KybApiBrowserCard } from 'ui/molecules/KybApiBrowserCard/KybApiBrowserCard';
import { IntegrationCard } from 'ui/molecules/IntegrationCard/IntegrationCard';
import { StatsBlock } from 'ui/molecules/StatsBlock/StatsBlock';
import { CoverageSection } from 'ui/organisms/CoverageSection/CoverageSection';
import { CtaSection } from 'ui/organisms/CtaSection/CtaSection';
import { StepSwitcherSection } from 'ui/organisms/StepSwitcherSection/StepSwitcherSection';
import type { SwitcherStep } from 'ui/organisms/StepSwitcherSection/StepSwitcherSection.types';

import styles from './BankAccountVerificationPage.module.scss';

import { RegisPrompt } from './RegisPrompt';
import {
  getFaqItems,
  getFlowSteps,
  getHeroStats,
  getLayers,
  getOutcomes,
  getRiskQuestions,
} from './BankAccountVerificationPage.data';

export const BankAccountVerificationPage = () => {
  const { secondaryNavigation } = useContext(ContentContext);
  const { t } = useInt();

  const accessSteps: SwitcherStep[] = [
    {
      key: 'web',
      index: '01',
      tabTitle: t('Web platform'),
      tabSub: t('Verify from the dashboard'),
      title: t('Web platform.'),
      body: (
        <div className={styles.accessMeta}>
          <p>
            {t(
              'Run verifications one by one or upload an entire supplier file — results in seconds, no integration needed.',
            )}
          </p>

          {secondaryNavigation?.free_trial?.slug && (
            <Button type="blue" size="xl" route={secondaryNavigation.free_trial.slug}>
              {t('Request free trial')}
            </Button>
          )}
        </div>
      ),
      card: <BankVerificationCard />,
    },
    {
      key: 'api',
      index: '02',
      tabTitle: 'API (MCP)',
      tabSub: t('Embed in your payment flow'),
      title: 'API (MCP).',
      body: (
        <p>
          {t(
            'Verify an IBAN and its owner in a single call — or connect the Global Database MCP server and let your agents run verifications directly.',
          )}
        </p>
      ),
      card: (
        <KybApiBrowserCard
          method="POST"
          endpoint="/v1/accounts/verify"
          title={t('Bank account verification, one connection away.')}
          description={t(
            'Verify an IBAN and its owner in a single call — or connect the Global Database MCP server and let your agents run verifications directly.',
          )}
          dataReturned={[
            t('IBAN & account validity'),
            t('Payee name match + score'),
            t('Account ownership'),
            t('Registry entity record'),
            t('UBOs & directors'),
            t('PEP & sanctions flags'),
          ]}
        />
      ),
    },
    {
      key: 'regis',
      index: '03',
      tabTitle: 'Regis AI',
      tabSub: t('Ask in plain language'),
      title: 'Regis AI.',
      body: (
        <p className={styles.accessIntro}>
          <b>{t("Regis is Global Database's AI analyst.")}</b>{' '}
          {t(
            'Ask in plain language — it verifies bank accounts, pulls company records and screens the people behind them, from the same registry data.',
          )}
        </p>
      ),
      card: <RegisPrompt />,
    },
    {
      key: 'assistants',
      index: '04',
      tabTitle: t('AI assistants'),
      tabSub: 'Claude · ChatGPT · Cursor',
      title: t('AI assistants.'),
      body: <p className={styles.accessMetaNote}>{t('Free to install. Verify without leaving the chat.')}</p>,
      card: (
        <div className={styles.connectors}>
          <IntegrationCard
            icon="tools-2"
            toolName="Claude"
            company="Anthropic"
            category={t('Chat & Claude Code')}
            description={t(
              'Native MCP support across Claude.ai and Claude Code — verify accounts without leaving the chat.',
            )}
            href="https://www.globaldatabase.com/mcp"
          />

          <IntegrationCard
            icon="tools-1"
            toolName="ChatGPT"
            company="OpenAI"
            category={t('Custom MCP connector')}
            description={t('Add the Global Database MCP under Settings → Apps and screen any company inline.')}
            href="https://www.globaldatabase.com/mcp"
          />

          <IntegrationCard
            icon="cursor"
            toolName="Cursor"
            company="Anysphere"
            category={t('MCP in your editor')}
            description={t('Connect the MCP server in Cursor and verify payees straight from your workflow.')}
            href="https://www.globaldatabase.com/mcp"
          />
        </div>
      ),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <HeroSection
        align="left"
        title={t('Verify the bank account. And the company behind it.')}
        description={t(
          'We validate the receiving bank account through trusted payment schemes, then instantly cross-reference the payee against official government registries — confirming legal status, corporate structure, and ownership.',
        )}
        actions={
          secondaryNavigation?.free_trial?.slug && (
            <Button type="blue" size="xxl" route={secondaryNavigation.free_trial.slug}>
              {t('Verify bank account')}
            </Button>
          )
        }
        stats={getHeroStats(t)}
        media={<BankVerifyHeroDemo />}
        mediaPosition="right"
      />

      <OurCustomersV2 hasBackground={false} />

      <StepSwitcherSection
        title={t('From bank details to full company intelligence. One flow.')}
        description={t(
          "Start with a bank account. End with full confidence in who you're paying — and get alerted if anything changes.",
        )}
        steps={getFlowSteps(t)}
      />

      <VerificationLayers
        title={t('One verification. Three layers deep.')}
        description={t(
          'The account is verified through bank verification schemes and banking partners. Then the entity and its owners are verified against official government registry data. Most vendors stop at layer one — we run all three.',
        )}
        note={
          <>
            <b>{t('The right method for each layer.')}</b>{' '}
            {t(
              'Payment schemes — SCAN, VoP and equivalents — confirm the account. Government registries in 200+ countries confirm the company and the people behind it. One call runs both.',
            )}
          </>
        }
        layers={getLayers(t)}
      />

      <Section
        layout="split"
        splitAlign="top"
        title={t('Why verify the company, not just the bank account?')}
        description={t(
          "A fraudster's bank details can be perfectly valid — the account exists, the name matches, the payment would clear. The fraud shows in the company behind it. The registry record answers what the account check can't:",
        )}
        actions={
          <ol className={styles.riskList}>
            {getRiskQuestions(t).map((question, i) => (
              <li key={question}>
                <span className={styles.riskNum}>{String(i + 1).padStart(2, '0')}</span>
                {question}
              </li>
            ))}
          </ol>
        }
      >
        <RiskScanDemo />
      </Section>

      <CoverageSection
        title={t('Four outcomes. Zero ambiguity.')}
        description={t('Every bank account verification returns exactly one of these four results.')}
        rows={getOutcomes(t)}
      />

      <StatsBlock
        title={t('Direct bank verification across 49+ countries.')}
        description={t(
          'Real-time verification via local banking infrastructure and the European Payments Council VoP scheme. Company intelligence across 200+ countries.',
        )}
        features={[
          { value: '49', suffix: '+', title: t('Direct Verification') },
          { value: '10', suffix: 'M+', title: t('Daily Capacity') },
        ]}
      />

      <StepSwitcherSection eyebrow={t('Access')} title={t('How to access this data.')} steps={accessSteps} />

      <CtaSection
        spotlight
        title={t('Every payment deserves a verified payee.')}
        description={t(
          'Pay per verification, with volume pricing at scale. Enterprise teams get dedicated support, SLAs and custom coverage.',
        )}
        actions={
          secondaryNavigation?.book_demo?.slug && (
            <Button type="blue" size="xxl" route={secondaryNavigation.book_demo.slug}>
              {t('Book a demo')}
            </Button>
          )
        }
      />

      <Faq className={styles.faq} title={t('Frequently asked questions.')} data={getFaqItems(t)} />
    </div>
  );
};
